import { useStore } from '@/store/StoreContext'
import { useT }     from '@/i18n/I18nContext'
import { useAuth }  from '@/auth/AuthContext'

// ─── Helpers ──────────────────────────────────────────────────────────────────
const CHILD_PROG = { c1:68, c2:54, c3:42, c4:79 }

function ProgressRing({ pct, size=52, stroke=4, color="#1A4A6E" }) {
  const r=(size-stroke*2)/2, circ=2*Math.PI*r, off=circ-(pct/100)*circ
  return (
    <svg width={size} height={size} style={{transform:"rotate(-90deg)"}}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E4EBF2" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round"
        style={{transition:"stroke-dashoffset .6s ease"}}/>
    </svg>
  ) 
}

function Avatar({ child, size=40, ring=false }) {
  const pct = CHILD_PROG[child.id] || 60
  return (
    <div style={{position:"relative",width:size,height:size,flexShrink:0}}>
      {ring && <div style={{position:"absolute",inset:-3}}><ProgressRing pct={pct} size={size+6} stroke={3} color={child.color}/></div>}
      <div style={{width:size,height:size,borderRadius:"50%",background:child.color,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:800,fontSize:size*.34}}>{child.initials}</div>
    </div>
  )
}

function relTime(iso) {
  const diff = Date.now() - new Date(iso).getTime()
  const m=Math.floor(diff/60000), h=Math.floor(diff/3600000), d=Math.floor(diff/86400000)
  if (m<2)  return "just now"
  if (m<60) return `${m} min ago`
  if (h<24) return `${h} hr ago`
  return `${d} day${d===1?"":"s"} ago`
}

const MOD_INFO = [
  {id:"collection",icon:"🗂️",color:"#1A4A6E",accent:"#2E7DB8",bg:"#EBF4FB",tag:"tag.library"},
  {id:"flashcard", icon:"🃏",color:"#1B6B45",accent:"#27AE60",bg:"#EDF7F2",tag:"tag.print"},
  {id:"worksheet", icon:"📝",color:"#6B2D8B",accent:"#9B59B6",bg:"#F5EEF8",tag:"tag.print"},
  {id:"activity",  icon:"🖼️",color:"#7D4800",accent:"#D4AC0D",bg:"#FEF9E7",tag:"tag.print"},
  {id:"storybook", icon:"📖",color:"#8B3A2A",accent:"#C0392B",bg:"#FDEDEC",tag:"tag.print"},
  {id:"workplan",  icon:"📅",color:"#0D5C63",accent:"#17A589",bg:"#E8F8F5",tag:"tag.planning"},
  {id:"progress",  icon:"📊",color:"#5D4037",accent:"#8D6E63",bg:"#EFEBE9",tag:"tag.assessment"},
  {id:"readiness", icon:"🎮",color:"#1A237E",accent:"#3F51B5",bg:"#E8EAF6",tag:"tag.assessment"},
]

const NCF_IDS    = ["physical","socio","cognitive","language","numeracy"]
const NCF_COLORS = {physical:"#1B6B45",socio:"#C0392B",cognitive:"#6B2D8B",language:"#1A4A6E",numeracy:"#D4AC0D"}
const NCF_PCTS   = {physical:72,socio:58,cognitive:65,language:81,numeracy:49}

export default function Dashboard({ onNav }) {
  const t = useT()
  const { user } = useAuth()
  const { collection, childList, childTotal, activity, workPlans, publicSrtTotal } = useStore()
  const today = new Date().toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"})
  const isSuperAdmin = user?.role === 'superadmin'

  return (
    <div>
      <style>{`
        @media (min-width: 600px) {
          .dash-pad { padding: 28px 26px 80px !important; }
        }
        /* Stat cards: 2-col on mobile, 4-col on desktop */
        .dash-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 13px;
        }
        @media (min-width: 640px) {
          .dash-stats { grid-template-columns: repeat(4,1fr); gap: 13px; }
        }
        /* Main 2-col layout: single col on mobile */
        .dash-main {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
        }
        @media (min-width: 860px) {
          .dash-main { grid-template-columns: 1fr 308px; gap: 20px; }
        }
        /* Module grid: 1-col on mobile, 2-col on tablet+ */
        .dash-mods {
          display: grid;
          grid-template-columns: 1fr;
          gap: 9px;
        }
        @media (min-width: 480px) {
          .dash-mods { grid-template-columns: repeat(2,1fr); }
        }
        /* Heading size */
        .dash-h1 { font-size: 22px; }
        @media (min-width: 600px) { .dash-h1 { font-size: 27px; } }
      `}</style>

      <div className="dash-pad" style={{padding:"20px 16px 80px",maxWidth:1120,margin:"0 auto"}}>

        <div style={{marginBottom:20}}>
          <div className="dash-h1" style={{fontFamily:"'Playfair Display',serif",fontWeight:900,color:"var(--navy)",marginBottom:5}}>
            {t("dash.greeting")}
          </div>
          <div style={{fontSize:13,color:"var(--mist)"}}>{today} · {t("platform.name")}</div>
        </div>

        {/* ── STAT CARDS ── */}
        <div className="dash-stats" style={{marginBottom: isSuperAdmin ? 10 : 20}}>
          {[
            {lk:"dash.children", sk:"dash.active",   val:childTotal,              loading:childTotal===null, icon:"👤", c:"var(--blue)"},
            {lk:"dash.images",   sk:"dash.in_coll",  val:(collection||[]).length, loading:false,             icon:"🖼️", c:"var(--green)"},
            {lk:"dash.workplans",sk:"dash.saved_pl", val:(workPlans||[]).length,  loading:false,             icon:"📅", c:"#6B2D8B"},
            {lk:"dash.logs",     sk:"dash.recorded", val:(activity||[]).length,   loading:false,             icon:"📋", c:"var(--red)"},
          ].map((s,i) => (
            <div key={s.lk} style={{background:"white",borderRadius:13,padding:"14px 16px",border:"1.5px solid var(--border)",animationDelay:`${i*65}ms`}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <span style={{fontSize:18}}>{s.icon}</span>
                <span style={{fontSize:9,color:"var(--mist)",fontWeight:600,background:"var(--fog)",padding:"2px 6px",borderRadius:7}}>{t(s.sk)}</span>
              </div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:900,color:s.c}}>
                {s.loading ? <span style={{fontSize:18,color:"var(--mist)"}}>—</span> : s.val}
              </div>
              <div style={{fontSize:11,color:"var(--mist)",fontWeight:600,marginTop:2}}>{t(s.lk)}</div>
            </div>
          ))}
        </div>

        {/* ── PUBLIC SRT CARD — superadmin only ── */}
        {isSuperAdmin && (
          <div style={{marginBottom:20}}>
            <div
              onClick={() => onNav('public-srt')}
              style={{
                background:'white', borderRadius:13, padding:'14px 16px',
                border:'2px solid #BBF7D0', cursor:'pointer',
                display:'flex', alignItems:'center', gap:14,
                transition:'box-shadow .15s, transform .15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(22,163,74,.15)'
                e.currentTarget.style.transform = 'translateY(-2px)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = ''
                e.currentTarget.style.transform = ''
              }}
            >
              <div style={{width:44,height:44,borderRadius:11,background:'#DCFCE7',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,flexShrink:0,border:'2px solid #16A34A44'}}>🌐</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:900,color:'#16A34A',lineHeight:1}}>
                  {publicSrtTotal ?? '—'}
                </div>
                <div style={{fontSize:11,color:'var(--mist)',fontWeight:600,marginTop:2}}>Public SRT Registrations</div>
              </div>
              <div style={{fontSize:11,fontWeight:700,color:'#16A34A',background:'#DCFCE7',padding:'5px 11px',borderRadius:20,flexShrink:0,whiteSpace:'nowrap'}}>
                View Reports →
              </div>
            </div>
          </div>
        )}

        {/* ── MAIN GRID ── */}
        <div className="dash-main">

          <div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,fontWeight:700,color:"var(--navy)",marginBottom:13}}>
              {t("dash.modules")}
            </div>
            <div className="dash-mods">
              {MOD_INFO.map(m => (
                <div key={m.id} onClick={() => onNav(m.id)}
                  style={{background:"white",borderRadius:13,overflow:"hidden",border:"1.5px solid var(--border)",cursor:"pointer",transition:"transform .15s,box-shadow .15s"}}
                  onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 6px 20px rgba(0,0,0,.09)"}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow=""}}>
                  <div style={{height:4,background:`linear-gradient(90deg,${m.color},${m.accent})`}}/>
                  <div style={{padding:"12px 14px"}}>
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:7}}>
                      <div style={{width:34,height:34,borderRadius:9,background:m.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>{m.icon}</div>
                      <div style={{display:"flex",alignItems:"center",gap:4}}>
                        <span style={{fontSize:9,fontWeight:700,background:m.bg,color:m.color,padding:"2px 7px",borderRadius:7}}>{t(m.tag)}</span>
                        <span style={{fontSize:13,color:m.accent}}>→</span>
                      </div>
                    </div>
                    <div style={{fontWeight:700,fontSize:12.5,color:"var(--navy)",marginBottom:3,lineHeight:1.3}}>{t(`mod.${m.id}`)}</div>
                    <div style={{fontSize:11,color:"var(--mist)",lineHeight:1.5}}>{t(`desc.${m.id}`)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:16}}>

            <div style={{background:"white",borderRadius:13,padding:17,border:"1.5px solid var(--border)"}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:"var(--navy)",marginBottom:11}}>{t("dash.quick")}</div>
              {[
                {k:"qa.upload",   icon:"📁",id:"collection"},
                {k:"qa.flashcard",icon:"🃏",id:"flashcard"},
                {k:"qa.progress", icon:"📊",id:"progress"},
                {k:"qa.workplan", icon:"📅",id:"workplan"},
                {k:"qa.readiness",icon:"🎮",id:"readiness"},
                ...(isSuperAdmin ? [{k:"qa.public_srt",icon:"🌐",id:"public-srt"}] : []),
              ].map(q => (
                <button key={q.id} onClick={() => onNav(q.id)}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:9,padding:"8px 11px",marginBottom:5,background:"var(--fog)",border:"none",borderRadius:9,cursor:"pointer",transition:"background .15s"}}
                  onMouseEnter={e=>e.currentTarget.style.background="#E4EBF2"}
                  onMouseLeave={e=>e.currentTarget.style.background="var(--fog)"}>
                  <span style={{fontSize:13}}>{q.icon}</span>
                  <span style={{fontSize:12,fontWeight:600,color:"var(--ink)"}}>{t(q.k)}</span>
                  <span style={{marginLeft:"auto",fontSize:12,color:"var(--mist)"}}>→</span>
                </button>
              ))}
            </div>

            <div style={{background:"white",borderRadius:13,padding:17,border:"1.5px solid var(--border)",flex:1}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:"var(--navy)",marginBottom:11}}>{t("dash.recent")}</div>
              {(activity||[]).length === 0 && (
                <div style={{fontSize:12,color:"var(--mist)",textAlign:"center",padding:"20px 0"}}>No activity yet</div>
              )}
              {(activity||[]).slice(0,6).map(a => (
                <div key={a.id} style={{display:"flex",alignItems:"flex-start",gap:9,marginBottom:10}}>
                  <div style={{width:29,height:29,borderRadius:8,background:"var(--fog)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0}}>{a.icon||"📌"}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--ink)",lineHeight:1.4}}>{a.action}</div>
                    <div style={{fontSize:10,color:"var(--mist)",marginTop:2}}>{a.child} · {relTime(a.time)}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Children panel */}
            <div style={{background:"white",borderRadius:13,padding:17,border:"1.5px solid var(--border)"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:11}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:"var(--navy)"}}>{t("nav.children")}</div>
                <button onClick={() => onNav("children")} style={{fontSize:11,color:"var(--sky)",fontWeight:700,background:"none",border:"none",cursor:"pointer"}}>{t("action.view_all")}</button>
              </div>
              {(childList||[]).map(c => (
                <div key={c._id||c.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:9}}>
                  <Avatar child={c} size={36} ring/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:12,color:"var(--ink)"}}>{c.name}</div>
                    <div style={{fontSize:10,color:"var(--mist)",overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {c.age != null ? `Age ${c.age}` : '—'}
                      {' · '}
                      {(c.disability||c.disabilities||[])[0]||'—'}
                    </div>
                  </div>
                  <div style={{fontWeight:800,fontSize:14,color:c.color,flexShrink:0}}>
                    {CHILD_PROG[c.id]||60}%
                    <span style={{fontSize:9,color:"var(--mist)",fontWeight:500}}> overall</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{background:"white",borderRadius:13,padding:17,border:"1.5px solid var(--border)"}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:"var(--navy)",marginBottom:3}}>{t("dash.ncf_progress")}</div>
              <div style={{fontSize:10,color:"var(--mist)",marginBottom:13}}>{t("dash.class_average")}</div>
              {NCF_IDS.map(d => (
                <div key={d} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:11.5,fontWeight:600,color:"var(--ink)"}}>{t(`domain.${d}`)}</span>
                    <span style={{fontSize:11.5,fontWeight:800,color:NCF_COLORS[d]}}>{NCF_PCTS[d]}%</span>
                  </div>
                  <div style={{height:6,background:"var(--fog)",borderRadius:5,overflow:"hidden"}}>
                    <div style={{width:`${NCF_PCTS[d]}%`,height:"100%",background:NCF_COLORS[d],borderRadius:5,transition:"width .6s ease"}}/>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

      </div>
    </div>
  )
}